<template>
    <div v-if="loading" class="uk-text-center uk-padding fw-form-loading">
        <img src="/plugins/kosmoskosmos/festiware/assets/images/festiware_loading-spinner_v1.svg"
             alt="Loading"
             style="width:96px;height:96px"/>
    </div>
    <div v-if="message" class="uk-margin-small-top uk-flex-center uk-flex-middle uk-flex" uk-height-viewport="expand: true">
        <div class="uk-width-medium" v-html="message"></div>
    </div>
    <template v-else>
        <div class="intro uk-margin" v-if="introContent && !isUpdate" v-html="introContent"></div>
        <p v-if="noVisibleFields" class="uk-text-muted uk-margin-small-top">{{ noVisibleFieldsText }}</p>
        <Vueform v-show="!noVisibleFields" class="vue-form uk-position" ref="form" v-bind="vueform" :displayErrors="false" validate-on="change|step" :endpoint="submitForm"></Vueform>
    </template>
</template>

<script>
import {nextTick} from 'vue';
import { Vueform, useVueform} from '@vueform/vueform'
import helper from "./helper";
import {dateTextToIso} from "./customElements/plugins";

export default {
    mixins: [Vueform, helper],
    setup: useVueform,
    props: {
        formDefId: {type: String},
        lang: {type: String, default: 'de'},
        isUpdate: {type: Boolean, default: false},
        componentName: {type: String, required: true},
        passThroughData: {type: Object, default: {}},
        applicationId: {default: false},
        forceNavDropdown: {type: Boolean, default: false},
    },
    data: () => ({vueform: {}, loading: true, listeners: {update: undefined, clear: undefined, passThrough: undefined, checkDirtyForm: undefined}, initialData: {}, message: false, noVisibleFields: false, mobileTabs: {show: false, selected: '', options: []}, buttonLabelRules: [], defaultButtonLabels: {}}),
    computed: {
        introContent() {
            return this.resolveLocale(this.vueform.intro);
        },
        noVisibleFieldsText() {
            return this.lang === 'de'
                ? 'Aktuell sind keine Felder zur Bearbeitung verfügbar.'
                : 'No fields are currently available for editing.';
        },
    },
    mounted() {
        this.$vueform.i18n.locale = this.lang;
        this.addListerners();
        this.interceptAjaxErrors();
        this.getFormSettings();
    },
    unmounted() {
        window.removeEventListener('updateFormDataExternally', this.listeners.update, false);
        window.removeEventListener('clearFormExternally', this.listeners.clear, false);
        window.removeEventListener('updatePassThroughExternally', this.listeners.passThrough, false);
        window.removeEventListener('checkDirty_updateform', this.listeners.checkDirtyForm, false);
    },
    methods: {
        resolveLocale(value) {
            if (!value) return null;
            // Use `||` so empty strings fall through to the next locale or null.
            // The previous `??` (nullish-coalesce) passed `''` back unchanged, which
            // surfaced as a check-icon-only toast when a form definition's
            // update_success_response was set to {"de":"","en":""}.
            if (typeof value === 'object') return value[this.lang] || value.de || value.en || null;
            return value;
        },
        setActiveTab(el) {
            this.$refs.form.tabs$.tabs$[el].select()
        },
        /**
         * The elements the form would render if every tab/step were open.
         *
         * Deliberately NOT `el.visible`: Vueform defines that as
         * `available && !hidden && active`, and selecting a tab/step deactivates the
         * elements of all the others. `visible` therefore only ever describes the
         * CURRENTLY selected tab/step — useless for judging the form as a whole.
         *
         * `available` is the conditions verdict, `hidden` the imperative show()/hide()
         * state; both are independent of tab/step selection. Compared against `false` /
         * `true` rather than truthiness so an element type that exposes neither counts as
         * renderable — erring towards showing the form, never towards hiding it.
         *
         * Membership is by component identity, which is what makes it comparable to a
         * tab's/step's `children$` — see the gotcha on leaveEmptyTabOrStep().
         */
        renderableElements() {
            return new Set(
                Object.values(this.$refs.form?.elements$ ?? {})
                    .filter((el) => el.available !== false && el.hidden !== true)
            );
        },
        /**
         * Should this form still offer a submit button?
         *
         * Yes, unless the form HAS inputs and every one of them is currently conditioned
         * away — the shape a deadline lock produces: one static "the deadline has passed"
         * notice left standing, every field hidden, and a submit button underneath
         * promising a next step that does not exist.
         *
         * @gotcha The "has inputs at all" half is not redundant, it is what keeps a
         *   deliberate acknowledge-only form working: static text plus a button whose
         *   submit fires the `portal_update` trigger chain in php-core
         *   (VueFormSubmitController -> TriggerDispatcher). Such a form never had an input
         *   to begin with, so measuring only "nothing editable is visible" would strip its
         *   one button and silently break the acknowledgement — for every instance, not
         *   just the one that wanted a lock.
         *
         * Display-only types are excluded by element type: `static` covers html fields and
         * calculated (twig) fields alike — php-core renders every twig field as `static`
         * since 4.29.23.8 — `button` is the submit itself, and `hidden` carries values the
         * contributor never sees (record id, gate fields). An unrecognised type counts as
         * an input, so a future element type can only ever make the button appear, never
         * vanish. Same defensive direction as renderableElements(): in doubt, show.
         *
         * @gotcha The type lives on the element as `el.type`. A mounted Vueform element has
         *   NO `schema` property — reading `el.schema?.type` yields undefined for every
         *   element, which lands each of them in the "is an input" bucket and makes this
         *   guard answer `true` forever: the button never disappears and nothing looks
         *   broken. Cost an hour on 2026-08-20, and a green unit test that had mocked
         *   `el.schema.type` and so only confirmed the wrong assumption. `el.schema?.type`
         *   is kept as a fallback in case a future release exposes it that way, but `type`
         *   is the one that answers today. Verified against the live element inventory:
         *   static / text / select / checkbox / hidden / button.
         *
         * @gotcha Must not count `button` or `static` as an input, or this guard would
         *   depend on the very elements it decides about and Vueform's condition graph
         *   would feed back on itself.
         */
        hasSubmittableInput() {
            const els = Object.values(this.$refs.form?.elements$ ?? {});
            if (!els.length) return true;   // not mounted yet — never hide on a guess
            const displayOnly = ['static', 'button', 'hidden'];
            const inputs = els.filter((el) => !displayOnly.includes(el.type ?? el.schema?.type));
            if (!inputs.length) return true;   // acknowledge-only form — keep its button
            return inputs.some((el) => el.available !== false && el.hidden !== true);
        },
        /**
         * Move off a first tab/step whose every element is conditioned away.
         *
         * Vueform drops a tab/step from the navigation only when the tab/step ITSELF
         * carries an unmet condition. One whose ELEMENTS are all hidden stays in the
         * navigation and stays selected, leaving the contributor on a blank page with no
         * hint that the rest of the form exists.
         *
         * @gotcha This is not cosmetic. Together with the `visible` trap above it made a
         *   whole form unreachable: Kessel Festival's vendor update form opens on an
         *   "Abrechnung" tab conditioned on `product_category == essensstand_foodtruck`,
         *   so for the other 63 of 117 vendors every element measured as invisible, the
         *   empty-state text replaced the form, and `v-show` took the tab navigation with
         *   it — no way to reach the post-event survey on the following tab.
         *   HelpScout 3418621772, 2026-08-14.
         *
         * @gotcha A tab's/step's `children$` is a lodash `filter()` over the form's
         *   `elements$` — on an object that returns an ARRAY, not a keyed object. So its
         *   keys are `0, 1, 2…`, never element names; match on the component identity the
         *   filter preserves instead. `Object.values()` covers both shapes should a future
         *   Vueform release make it keyed again.
         */
        leaveEmptyTabOrStep(renderable) {
            const form$ = this.$refs.form;
            // FormSteps enables steps one by one, so jumping forward needs goTo(name, true);
            // FormTabs has no such gate and is selected directly.
            const container = form$?.steps$
                ? {items: form$.steps$.steps$, goTo: (name) => form$.steps$.goTo(name, true)}
                : form$?.tabs$
                    ? {items: form$.tabs$.tabs$, goTo: (name) => form$.tabs$.tabs$[name].select()}
                    : null;
            if (!container?.items) return;

            const hasContent = (name) => Object.values(container.items[name]?.children$ ?? {})
                .some((el) => renderable.has(el));

            const names = Object.keys(container.items);
            const current = names.find((name) => container.items[name]?.active);
            if (!names.length || (current && hasContent(current))) return;

            const target = names.find(hasContent);
            if (target && target !== current) container.goTo(target);
        },
        interceptAjaxErrors() {
            // Intercept fetch requests to capture error responses before OctoberCMS processes them
            const originalFetch = window.fetch;
            window.fetch = async (...args) => {
                const response = await originalFetch(...args);

                // Clone the response so we can read it without consuming the original
                const clone = response.clone();

                // If it's a 400 error, try to parse and store the error message
                if (response.status === 400 || response.status === 422) {
                    try {
                        const data = await clone.json();
                        if (data.__ajax && data.__ajax.message) {
                            // Store the error message globally so we can access it in the error callback
                            window.__lastAjaxError = data.__ajax.message;
                        }
                    } catch (e) {
                        // Silently fail if we can't parse the error response
                    }
                }

                return response;
            };
        },
        addListerners() {
            this.listeners.update = (data) => setTimeout(() => {
                this.$refs.form.update(data.detail)
                setTimeout(() => this.$refs.form.update(data.detail))
            });
            this.listeners.clear = () => {
	            this.message = false;
                if (this.$refs.form) this.$refs.form.clear;
            }
            this.listeners.passThrough = (event) => {
                setTimeout(() => Object.keys(event.detail).forEach((key) => this.passThroughData[key] = event.detail[key]));
            }
            this.listeners.checkDirtyForm = (event) => {
                if (this.isUpdate && Object.keys(this.initialData).length > 0) {
                    // form.update() marks fields dirty on load, so .dirty is always true in
                    // edit mode even without user changes. Compare against the saved snapshot instead.
                    event.isDirty = JSON.stringify(this.$refs.form?.data ?? {}) !== JSON.stringify(this.initialData);
                } else {
                    event.isDirty = this.$refs.form?.dirty;
                }
            }
            window.addEventListener('updateFormDataExternally', this.listeners.update);
            window.addEventListener('clearFormExternally', this.listeners.clear);
            window.addEventListener('updatePassThroughExternally', this.listeners.passThrough);
            window.addEventListener('checkDirty_updateform', this.listeners.checkDirtyForm);
        },
        getFormSettings() {
            this.doOctoberRequest('onGetFormSettings', {formId: this.formDefId, isUpdate: this.isUpdate, applicationId: this.applicationId}, false, true).then(result => {
                // console.log(result)
                // add onChange listener to owner_person field to automatically update repeater fields
                if (typeof result.schema != 'undefined' && typeof result.schema.owner_person != 'undefined' && result.repeatableField && result.personFieldBeforeRepeater) {
                    for (let key in result.schema.owner_person.schema) {
                        result.schema.owner_person.schema[key].onChange = function(newVal, oldVal, el$) {
                            for (let i in el$.form$.el$(result.repeatableField).value) {
                                if (el$.form$.el$(result.repeatableField).value[i].is_owner_person && typeof el$.form$.el$(result.repeatableField).value[i][el$.name] != 'undefined') {
                                    el$.form$.el$(result.repeatableField).value[i][el$.name] = newVal
                                }
                            }
                        }
                    }
                }

                this.applyTwigFieldFixes(result.schema, result.data || {});
                this.markRequiredCheckboxes(result.schema);
                this.normalizeOptionValues(result.schema, result.data || {});
                result.customSaveLabel = this.resolveLocale(result.button_label) || null;
                // PHP setTabButtons passes button_label through to the schema submit
                // button's buttonLabel verbatim — that may be a multilingual {de,en}
                // array, which Vueform cannot render (→ blank, zero-width button).
                // Resolve it to the active locale here. The rules path below recreates
                // its own buttons with resolved labels, so doing this first is safe.
                for (const field of Object.values(result.schema ?? {})) {
                    if (!field?.submits) continue;
                    // button_label may arrive as a multilingual {de,en} map (Vueform
                    // cannot render an object) — resolve it to the active locale.
                    if (field.buttonLabel && typeof field.buttonLabel === 'object') {
                        field.buttonLabel = this.resolveLocale(field.buttonLabel);
                    }
                    // …and it may be blank when the admin configured the custom label
                    // but left it empty ({de:"",en:""}) — fall back to the default save
                    // label so the submit button is never rendered as a blank chip.
                    if (!field.buttonLabel || (typeof field.buttonLabel === 'string' && !field.buttonLabel.trim())) {
                        field.buttonLabel = this.$refs.form?.translations?.festiware?.tabs?.save
                            || (this.lang === 'en' ? 'Save' : 'Speichern');
                    }
                }
                if (result.button_label_rules?.length) {
                    this.buttonLabelRules = result.button_label_rules;
                    const rules = result.button_label_rules;
                    for (const [key, field] of Object.entries({...result.schema})) {
                        if (!field?.submits) continue;
                        const originalField = {...field}; // original label from PHP setTabButtons
                        delete result.schema[key];
                        // Update tab/step elements arrays
                        for (const tabKey of ['tabs', 'steps']) {
                            if (!result[tabKey]) continue;
                            for (const tab of Object.values(result[tabKey])) {
                                const idx = (tab.elements ?? []).indexOf(key);
                                if (idx === -1) continue;
                                tab.elements.splice(idx, 1,
                                    ...rules.map((_, i) => `${key}_rule_${i}`),
                                    `${key}_fallback`);
                            }
                        }
                        // One conditional button per rule
                        rules.forEach((rule, i) => {
                            const label = this.resolveLocale(rule.label) || rule.label;
                            const cond = (rule.condition || '').trim();
                            const prevConds = rules.slice(0, i).map(r => (r.condition||'').trim()).filter(Boolean);
                            const conditions = cond
                                ? [(form$) => { try { return this.evalPortalCondition(cond, form$.data ?? {}); } catch(e) { console.warn('[fw] button condition error:', cond, e.message); return false; } }]
                                : prevConds.length
                                    ? [(form$) => { try { return prevConds.every(c => !this.evalPortalCondition(c, form$.data ?? {})); } catch { return false; } }]
                                    : [];
                            result.schema[`${key}_rule_${i}`] = { ...originalField, buttonLabel: label, conditions };
                        });
                        // Fallback button: shows with original label when no rule matches
                        // OR when any condition throws. Without this catch-all, a form
                        // whose only rule has a non-empty condition would render zero
                        // submit buttons whenever that condition evaluates to false.
                        // buttonLabel is resolved here because PHP may pass a multilingual
                        // array (default_button_label) which Vueform cannot render natively.
                        result.schema[`${key}_fallback`] = {
                            ...originalField,
                            buttonLabel: this.resolveLocale(originalField.buttonLabel) || originalField.buttonLabel,
                            conditions: [(form$) => {
                                try {
                                    const anyMatched = rules.some((r, i) => {
                                        const c = (r.condition || '').trim();
                                        if (!c) {
                                            const prev = rules.slice(0, i).map(p => (p.condition || '').trim()).filter(Boolean);
                                            return prev.every(pc => !this.evalPortalCondition(pc, form$.data ?? {}));
                                        }
                                        return this.evalPortalCondition(c, form$.data ?? {});
                                    });
                                    return !anyMatched;
                                } catch { return true; }
                            }]
                        };
                    }
                }
                // A form whose every input is conditioned away has nothing left to submit.
                // Pressing the button posted a payload with no field in it. See
                // hasSubmittableInput() for why "has inputs at all" is part of the test.
                //
                // Guarded here rather than by dropping the buttons from the schema, because
                // whether a field is conditioned away is only known at runtime and can
                // change with every keystroke. As a condition it re-evaluates with the rest
                // of them. Appended to the existing conditions array, which Vueform ANDs —
                // the per-rule label conditions above keep working unchanged.
                //
                // `separator_*` goes with it: PHP setTabButtons emits `separator_submit`
                // (flat form) or `separator_<tabId>` (tabbed) as a decorative <hr> in front
                // of the button, and those are the only separators it makes. Left alone, a
                // rule line floats under the notice with nothing beneath it.
                //
                // @gotcha Do NOT pair separator to button through the tab's `elements`
                //   array. An update form is frequently NOT tabbed at all — `tabs$` and
                //   `steps$` are both absent — so an adjacency walk over result.tabs /
                //   result.steps silently matches nothing and leaves the <hr> behind. Match
                //   on the key prefix instead; it holds for both shapes.
                for (const [key, field] of Object.entries(result.schema ?? {})) {
                    if (!field?.submits && !key.startsWith('separator_')) continue;
                    field.conditions = [...(field.conditions ?? []), () => this.hasSubmittableInput()];
                }
                this.vueform = result;
                this.vueform.isUpdate = this.isUpdate;
                this.vueform.providers = {doOctoberRequest: this.doOctoberRequest}
                if (this.isUpdate && this.vueform.tabs && Object.keys(this.vueform.tabs).length > 0) {
                    this.stripPerTabSubmitElements(this.vueform);
                }
                window.vueform = this.$refs.form
                this.$nextTick(() => this.$nextTick(() => {
                    const renderable = this.renderableElements();
                    const total = Object.keys(this.$refs.form?.elements$ ?? {}).length;
                    this.noVisibleFields = total > 0 && renderable.size === 0;
                    if (!this.noVisibleFields) {
                        this.leaveEmptyTabOrStep(renderable);
                    }
                }));
//                this.attachStaticFieldsContent(this.vueform.schema, result.data)
                this.initAutoStepSubmit();
                if (result.data) {
                    if (!this.isUpdate) {
                        UIkit.modal.confirm(
                            this.$refs.form.translations.festiware.use_stored_data, {i18n:{
                                cancel: this.$refs.form.translations.festiware.no,
                                ok: this.$refs.form.translations.festiware.yes
                            }}).then(() => {
                            this.setFormData(result.data);
                        }, () => {
                            this.doOctoberRequest('onRemoveStoredData');
                        })
                    } else {
                        this.setFormData(result.data);
                    }
                }
                if (this.isUpdate) {
                    document.addEventListener("keydown", (event) => {
                        if ((event.ctrlKey || event.metaKey) && event.key === "s") {
                            event.preventDefault();
                            this.$refs.form.submit();
                        }
                    });
                }
            }).catch(() => {
                // Error already handled and displayed in doOctoberRequest
                // Just catch here to prevent unhandled promise rejection
            }).finally(() => {
                // Hide the loading spinner once the form settings request has
                // settled (success or error) and the schema is rendered.
                this.loading = false;
            })
        },
        // A saved select value can come back with the Vueform 'option_' prefix
        // while the field's items are keyed without it — the role field keys its
        // items by the bare role id (e.g. "73") but the stored value loads as
        // "option_73", so the select would render empty. Coerce each loaded
        // option value to whichever variant (X / option_X) is an actual item key
        // before the data reaches Vueform. Fallback: keep the value untouched
        // when neither variant matches, so unknown/legacy values aren't lost.
        normalizeOptionValues(schema, data) {
            if (!schema || !data) return;
            const toItemKey = (val, keys) => {
                if (val === null || val === undefined || val === '') return val;
                const s = String(val);
                if (keys.includes(s)) return val;
                const flipped = s.startsWith('option_') ? s.slice(7) : 'option_' + s;
                if (keys.includes(flipped)) return flipped;
                // Matches neither form — a stale value left behind by a renamed/removed
                // option (e.g. an old import). Vueform would otherwise consider the
                // field "has a value" (so it renders its floating label) while the
                // select widget itself can't resolve a label for it and falls back to
                // its own placeholder — showing the same placeholder text twice. Treat
                // it as unset so the field renders as a normal empty field instead.
                return null;
            };
            const coerce = (field, container, name) => {
                if (!field || (field.type !== 'select' && field.type !== 'multiselect')) return;
                const keys = Object.keys(field.items || {});
                if (!keys.length) return;
                const cur = container[name];
                if (Array.isArray(cur)) container[name] = cur.map((v) => toItemKey(v, keys)).filter((v) => v !== null);
                else if (cur !== undefined) container[name] = toItemKey(cur, keys);
            };
            Object.keys(schema).forEach((name) => {
                const field = schema[name];
                if (!field) return;
                if (field.type === 'list' && field.object && field.object.schema && Array.isArray(data[name])) {
                    const rowSchema = field.object.schema;
                    data[name].forEach((row) => {
                        if (!row || typeof row !== 'object') return;
                        Object.keys(rowSchema).forEach((childName) => coerce(rowSchema[childName], row, childName));
                    });
                } else {
                    coerce(field, data, name);
                }
            });
        },
        async setFormData(data) {
            await nextTick();
            await this.$refs.form.update(data);

//            await this.$refs.form.load(data)
//            this.$refs.form.clean();  (this removing of dirty state lead to BIG problems .. fields not being submitted)
            if (data.form_step_index !== undefined && this.$refs.form.steps$) {
                for (let index = 0; index < data.form_step_index; index++) {
                    if (typeof this.$refs.form.steps$.steps$[index] !== 'undefined') {
                        this.$refs.form.steps$.steps$[index].complete()
                    }
                }
                this.$refs.form.steps$.goTo(data.form_step_index, true);
            }
            await nextTick();
            this.initialData = JSON.parse(JSON.stringify(this.$refs.form.data));
        },
        // NewForm.setTabButtons emits paired separator_<tabId>/<hr> + submit_<tabId>
        // schema entries and references both in tabs[<tabId>].elements. Drop both
        // sides so the nav-integrated save button can replace them cleanly.
        stripPerTabSubmitElements(form) {
            const schema = form.schema || {};
            const removedNames = new Set();

            Object.keys(schema).forEach((name) => {
                const node = schema[name];
                if (!node || typeof node !== 'object') return;
                if (node.type === 'button' && node.submits) {
                    removedNames.add(name);
                    const tabId = name.startsWith('submit_') ? name.slice('submit_'.length) : null;
                    if (tabId && schema['separator_' + tabId]) {
                        removedNames.add('separator_' + tabId);
                    }
                }
            });

            removedNames.forEach((name) => { delete schema[name]; });

            Object.values(form.tabs || {}).forEach((tab) => {
                if (Array.isArray(tab.elements)) {
                    tab.elements = tab.elements.filter((n) => !removedNames.has(n));
                }
            });
        },
        attachStaticFieldsContent(schema, data) {
            Object.entries(schema || {}).forEach(([name, node]) => {
                if (!node || typeof node !== 'object') return

                if (node.type === 'static') {
                    node.content = (el$) => data[name] ?? ''
                } else if (node.schema) {
                    this.attachStaticFieldsContent(node.schema, data)
                }
            })
        },
        async initAutoStepSubmit() {
            await nextTick();
            if (!this.$refs.form) return;
            this.transformTabNavIfNeeded();
            if (this.$refs.form.steps$) {
                this.$refs.form.steps$.on('next', async (step) => {
                    const formData = this.getChangedValues(this.$refs.form);
                    formData.form_step_index = step.index;
                    this.doOctoberRequest('onStoreStepData', formData)
                })
            }
        },
        getChangedValues(form$) {
            let formData = {form_def_id: this.formDefId, pass_through_data: this.passThroughData};
            if (this.isUpdate) {
                formData.is_update_request = true
                // FWT-1656: forward the bound group id so the backend resolves the update
                // target deterministically. The submit payload otherwise carries the binding
                // only via a hidden `id` field, which is absent for a contact person who
                // manages more than one group of the same type — letting php-core fall
                // through to creating a new orphan group.
                if (this.applicationId) formData.application_id = this.applicationId
            }
            Object.values(form$.elements$).forEach((el$) => {
                if ((!el$.isStatic && el$.dirty) || el$.type ==='hidden' || (!el$.dirty && el$.defaultValue)) {
                    formData = {...formData, ...el$.requestData}
                }
            })
            this.stripHiddenConditionalValues(formData, form$);
            this.normalizeDateTextValues(formData, form$);
            return formData;
        },

        // Converts date fields rendered as a masked text input ("Als Texteingabe im
        // Frontend", php-core emits `dateText: true`) from the displayed DD.MM.YYYY to
        // the YYYY-MM-DD that the flatpickr variant submits. php-core therefore never
        // has to know which variant rendered the field.
        //
        // Why this lives here and not in the element's own `requestData`: repeater rows
        // bypass per-child requestData entirely — the parent list aggregates values
        // straight from `parent.value.value[i]` (same root cause as documented on
        // stripHiddenConditionalValues below). A single walker covers both levels.
        //
        // Not handled (matches stripHiddenConditionalValues' scope): nested repeaters.
        normalizeDateTextValues(formData, form$) {
            const elements$ = form$.elements$ || {};
            for (const [name, el$] of Object.entries(elements$)) {
                if (el$ && el$.dateText && typeof formData[name] === 'string') {
                    formData[name] = dateTextToIso(formData[name]);
                    continue;
                }
                // List (repeater) — convert each row's date-text children.
                if (Array.isArray(formData[name]) && el$ && el$.isListType) {
                    formData[name].forEach((row, i) => {
                        if (!row || typeof row !== 'object') return;
                        for (const childName of Object.keys(row)) {
                            if (typeof row[childName] !== 'string') continue;
                            const childEl$ = form$.el$ && form$.el$(`${name}.${i}.${childName}`);
                            if (childEl$ && childEl$.dateText) {
                                row[childName] = dateTextToIso(row[childName]);
                            }
                        }
                    });
                }
            }
        },

        // Walks the form before submit and removes values of elements that are
        // currently `available === false` (hidden by an unmet trigger condition).
        //
        // Why this exists:
        //   Vueform's per-element `requestData` already skips unavailable
        //   elements at the TOP level (useData.js: `if (!available || !submit) return {}`).
        //   But for list (repeater) children, the parent's list-level requestData
        //   aggregates ALL children's values straight from `parent.value.value[i]`
        //   without re-checking each child's `available` — so a number field with
        //   default 0 that's hidden by a trigger inside a repeater row STILL ends
        //   up in the submit payload. See docs/sessions/2026-05-23_hidden-conditional-submit.md
        //
        // Behavior:
        //   - Top-level: removes keys whose elements report available=false.
        //     (Already filtered by Vueform itself; we keep this branch for
        //     defense in depth and to make the contract explicit.)
        //   - List rows: for every row index `i`, look up each child element via
        //     `form$.el$(`${listName}.${i}.${childName}`)` and delete the row key
        //     if that child reports available=false.
        //
        // Not handled (out of v1 scope):
        //   - Nested repeaters (list inside list): only one level of list children
        //     is walked. Vueform's wildcard substitution itself doesn't support
        //     nested-repeater triggers either, so this matches the schema contract.
        //   - Object/group elements with conditional children: same single-level
        //     walk. Extend here when a use-case shows up.
        stripHiddenConditionalValues(formData, form$) {
            const elements$ = form$.elements$ || {};
            for (const [name, el$] of Object.entries(elements$)) {
                if (el$ && el$.available === false && formData[name] !== undefined) {
                    delete formData[name];
                    continue;
                }
                // List (repeater) — walk each row's children individually.
                if (Array.isArray(formData[name]) && el$ && el$.isListType) {
                    formData[name].forEach((row, i) => {
                        if (!row || typeof row !== 'object') return;
                        for (const childName of Object.keys(row)) {
                            const childEl$ = form$.el$ && form$.el$(`${name}.${i}.${childName}`);
                            if (childEl$ && childEl$.available === false) {
                                delete row[childName];
                            }
                        }
                    });
                }
            }
        },
        evalPortalCondition(condition, formData) {
            // Twig `not X == Y` means `!(X == Y)`, unlike JS where `!X == Y` means `(!X) == Y`.
            // Convert `not FIELD OP VALUE` to `FIELD OP_NEGATED VALUE` to preserve precedence.
            let expr = condition
                .replace(/\bnot\s+(\w+)\s*==\s*/g,  '$1 !== ')
                .replace(/\bnot\s+(\w+)\s*!=\s*/g,  '$1 === ')
                .replace(/\bnot\s+/g, '!')
                .replace(/\band\b/g, '&&')
                .replace(/\bor\b/g,  '||');
            for (const [key, value] of Object.entries(formData ?? {})) {
                // Vueform select values carry an 'option_' prefix; strip it so
                // conditions match the clean values stored in the backend DB.
                const clean = typeof value === 'string' && value.startsWith('option_')
                    ? value.slice(7)
                    : value;
                const safe = typeof clean === 'string'
                    ? `'${clean.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
                    : JSON.stringify(clean ?? null);
                const safeKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                expr = expr.replace(new RegExp(`\\b${safeKey}\\b`, 'g'), safe);
            }
            try { return !!Function('"use strict";return (' + expr + ')')(); } catch { return false; }
        },
        async submitForm(FormData, form$) {
            const formData = this.getChangedValues(form$);
            this.doOctoberRequest('onSubmit', formData).then(result => {
                if (result.success) {
                    const successMessage = this.resolveLocale(result.success)
                        || this.$refs.form?.translations?.festiware?.update_success_default
                        || null;
                    const defaultSuccessMessage = this.$refs.form?.translations?.festiware?.update_success_default || null;
                    // When redirecting: show standard message immediately, pass the configured
                    // success text to the target module's done-button via __pendingPortalSuccessMessage.
                    // When not redirecting: show the configured success text immediately as before.
                    const detail = {redirectTo: result.redirect_to || null, successMessage: result.redirect_to ? (successMessage || null) : null};
                    if (this.isUpdate) {
                        if (result.redirect_to) {
                            if (defaultSuccessMessage) {
                                UIkit.notification({message: '<span uk-icon="check"></span> ' + defaultSuccessMessage, status: 'success', pos: 'top-center', timeout: 6000});
                            }
                        } else if (successMessage) {
                            UIkit.notification({message: '<span uk-icon="check"></span> ' + successMessage, status: 'success', pos: 'top-center', timeout: 6000});
                        }
                        if (detail.redirectTo && typeof window.handlePortalRedirect === 'function' && window.handlePortalRedirect(detail.redirectTo, detail.successMessage)) {
                            return;
                        }
                        this.$root.$el.dispatchEvent(new CustomEvent('formSubmitSuccess', {bubbles: true, detail}));
                    } else {
                        this.message = successMessage;
                        this.$root.$el.dispatchEvent(new CustomEvent('formSubmitSuccess', {bubbles: true, detail}));
                    }
                } else if (result.error_message) {
                    UIkit.modal.alert(this.$refs.form.translations.festiware.errors[result.error_message], {i18n: {ok: 'ok'}})
                }
            })
            return false;
        },
        doOctoberRequest(requestName, data, withFiles = true, inlineMessage = false) {
            if (this.vueform) this.vueform.loading = true;
            return new Promise((resolve, reject) => {
                $.request(this.componentName+'::'+requestName, {
                    data: data,
                    files: withFiles,
                    progressBar: true,
                    error: (jqXHR) => {
                        // OctoberCMS might wrap the error differently
                        // Try to get the actual error response
                        let errorData = jqXHR.responseJSON || jqXHR.response || jqXHR;
                        reject(errorData);
                    },
                    success: (data) => {
                        // Check if October AJAX returned an error
                        if (data.__ajax && data.__ajax.ok === false) {
                            reject(data);
                        } else if (data.error) {
                            reject(data.error);
                        } else {
                            resolve(data);
                        }
                    },
                });
            }).finally(() => {
                if (this.vueform) this.vueform.loading = false;
            }).catch(e => {
				let errorMessage = '';

				if (typeof e === 'string') {
					errorMessage = e;
				} else if (typeof e === 'object') {
					// Try various paths where the error message might be
					errorMessage = e.__ajax?.message
						|| e.X_OCTOBER_ERROR_MESSAGE
						|| e.responseText
						|| e.message
						|| e.error
						|| window.__lastAjaxError; // Check global intercepted error

					// Translation-key lookup: php-core's VueFormSubmitController
					// emits TRANSLATION KEYS (e.g. 'owner_person_not_defined',
					// 'generic_error') instead of localized strings — see that
					// controller's class docblock. If the raw message matches a key
					// under festiware.errors, replace it with the localized string.
					// Falls through to the raw message for unknown keys (legacy
					// backend strings still display as before).
					if (typeof errorMessage === 'string' && errorMessage) {
						const localized = this.$refs.form?.translations?.festiware?.errors?.[errorMessage];
						if (localized) {
							errorMessage = localized;
						}
					}

					// If still no message and object is empty, use a fallback
					if (!errorMessage || (typeof errorMessage === 'object' && Object.keys(errorMessage).length === 0)) {
						errorMessage = this.$refs.form?.translations?.festiware?.errors?.generic_error || 'An error occurred. Please try again later.';
					}

					// Clear the global error after using it
					if (window.__lastAjaxError) {
						delete window.__lastAjaxError;
					}
				} else {
					errorMessage = String(e);
				}

				if (inlineMessage) {
					this.message = '<div class="uk-text-center uk-margin-bottom"><i class="uk-text-warning" uk-icon="icon:warning;ratio:3"></i></div>'+errorMessage;
					// Re-throw to prevent .then() from being called
					throw new Error(errorMessage);
				} else {
		            UIkit.modal.alert(errorMessage, {i18n: {ok: 'ok'}});
					// Return empty object for backwards compatibility with modal alerts
					return {};
	            }
            });
        }
    }
}
</script>
